import { Formik, FormikProps } from 'formik';
import React, { MutableRefObject, useEffect, useMemo, useRef } from 'react';

import SwitchField from '@components/Form/SwitchField';
import { Theme } from '@interfaces/global';
import { Newsletter } from '@utils/backend-api/newsletters';

const AUTO_SELECT_NEWSLETTER_NAMES = [
  'The Information AM',
  'The Briefing',
  'The Weekend',
  'Dealmaker',
];

interface StepNewsletterProps {
  newsletters: Newsletter[];
  onStepComplete: () => void;
  stepSaveRef: MutableRefObject<(() => void | Promise<void>) | null>;
  subscribeAsync: (params: { newsletter_id: number | string }) => Promise<unknown>;
  unsubscribeAsync: (params: { newsletter_id: number | string }) => Promise<unknown>;
}

type FormValues = Record<string, boolean>;

const StepNewsletter = ({
  newsletters,
  onStepComplete,
  stepSaveRef,
  subscribeAsync,
  unsubscribeAsync,
}: StepNewsletterProps) => {
  const formikRef = useRef<FormikProps<FormValues>>(null);

  // Sort newsletters: auto-select first, then subscribed, then by order
  const sortedNewsletters = useMemo(() => {
    if (!newsletters?.length) return [];

    return [...newsletters].sort((a, b) => {
      // Sort by auto-select order first
      const aAutoIndex = AUTO_SELECT_NEWSLETTER_NAMES.indexOf(a.name);
      const bAutoIndex = AUTO_SELECT_NEWSLETTER_NAMES.indexOf(b.name);

      const aIsAuto = aAutoIndex !== -1;
      const bIsAuto = bAutoIndex !== -1;

      if (aIsAuto && !bIsAuto) return -1;
      if (!aIsAuto && bIsAuto) return 1;
      if (aIsAuto && bIsAuto) return aAutoIndex - bAutoIndex;

      // Then by subscription status
      if (a.currentUserSubscribed !== b.currentUserSubscribed) {
        return a.currentUserSubscribed ? -1 : 1;
      }

      // Finally by order property
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }, [newsletters]);

  const initialValues = useMemo(
    () =>
      sortedNewsletters.reduce<FormValues>((acc, newsletter) => {
        const isAutoSelected = AUTO_SELECT_NEWSLETTER_NAMES.includes(newsletter.name);
        const isSubscribed = newsletter.currentUserSubscribed;
        acc[`newsletter_${newsletter.id}`] = isAutoSelected || isSubscribed;
        return acc;
      }, {}),
    [sortedNewsletters]
  );

  useEffect(() => {
    stepSaveRef.current = async () => {
      const currentValues = formikRef.current?.values;
      if (!currentValues) return;

      await Promise.all(
        sortedNewsletters.map((newsletter) => {
          const isSelected = currentValues[`newsletter_${newsletter.id}`];
          return isSelected
            ? subscribeAsync({ newsletter_id: newsletter.id })
            : unsubscribeAsync({ newsletter_id: newsletter.id });
        })
      );
      onStepComplete();
    };

    return () => {
      stepSaveRef.current = null;
    };
  }, [sortedNewsletters, subscribeAsync, unsubscribeAsync, onStepComplete, stepSaveRef]);

  return (
    <div id="onboarding-newsletter" className="flex flex-col px-32 md:px-0">
      {/* Mobile header */}
      <div className="block md:hidden">
        <h4 className="m-0 font-suisse-screen text-headline-xxs">Stay Informed</h4>
        <p className="mb-24 mt-0 font-suisse-screen text-byline-md">
          Breaking news & deep analysis directly to your inbox.
        </p>
      </div>
      <Formik innerRef={formikRef} initialValues={initialValues} onSubmit={() => {}}>
        <ul className="mt-0 list-none p-0">
          {sortedNewsletters.map(({ frequency, name, id, cardImageUrl }) => (
            <li
              key={id}
              className="flex items-center justify-between border-0 border-b border-solid border-b-grayscale-beige py-16 font-suisse-intl text-description-sm text-grayscale-charcoal last:border-b-0 md:border-0"
            >
              <div className="flex items-start pr-16">
                <img
                  src={cardImageUrl}
                  alt={`${name} newsletter`}
                  className="mr-16 h-[60px] w-[60px] object-cover md:h-[80px] md:w-[80px]"
                />
                <div>
                  <span className="block font-suisse-intl-mono text-body-sm text-grayscale-gray">
                    {frequency}
                  </span>
                  <h3 className="m-0 font-suisse-works text-headline-xxs font-normal text-primary-black">
                    {name}
                  </h3>
                </div>
              </div>
              <SwitchField
                colorTheme={Theme.Dark}
                id={`newsletter-${id}-switch`}
                name={`newsletter_${id}`}
              />
            </li>
          ))}
        </ul>
      </Formik>
    </div>
  );
};

export default StepNewsletter;
